import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { SpaceProvider } from './context/SpaceContext';
import CookieBanner from './components/CookieBanner';
import CrispChat from './components/CrispChat';
import PWAInstallBanner from './components/PWAInstallBanner';
import AuthCallback from './pages/AuthCallback';
import './i18n';
import './App.css';

// Pages existantes
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import CourseGeneration from './pages/CourseGeneration';
import CourseOverview from './pages/CourseOverview';
import LessonPage from './pages/LessonPage';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Community from './pages/Community';
import Completion from './pages/Completion';
import FamilyDashboard from './pages/FamilyDashboard';
import OrgDashboard from './pages/OrgDashboard';
import BilanPage from './pages/BilanPage';
import VerifyEmail from './pages/VerifyEmail';

// Nouvelles pages
import ArticlesPage from './pages/ArticlesPage';
import ArticleDetail from './pages/ArticleDetail';
import ArticleEditor from './pages/ArticleEditor';
import CommunityForum from './pages/CommunityForum';
import ForumPost from './pages/ForumPost';
import NewForumPost from './pages/NewForumPost';
import SuperAdmin from './pages/SuperAdmin';
import Parcours from './pages/Parcours';
import Credits from './pages/Credits';
import Library from './pages/Library';
import CourseEditor from './pages/CourseEditor';
import CourseCreator from './pages/CourseCreator';
import PublicProfile from './pages/PublicProfile';
import CertificationVerify from './pages/CertificationVerify';

// Quiz
import QuizBrowse from './pages/QuizBrowse';
import QuizCreate from './pages/QuizCreate';
import QuizSolo from './pages/QuizSolo';
import QuizLive from './pages/QuizLive';
import QuizPresent from './pages/QuizPresent';

// Part 4, 5, 6
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Mentors from './pages/Mentors';
import MentorProfile from './pages/MentorProfile';
import CharteMentor from './pages/CharteMentor';
import Referentials from './pages/Referentials';
import ResetPassword from './pages/ResetPassword';

// Pages légales FR
import MentionsLegales from './pages/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import CGU from './pages/CGU';
import PolitiqueCookies from './pages/PolitiqueCookies';
import DonneesPersonnelles from './pages/DonneesPersonnelles';

// Pages légales EN
import MentionsLegalesEN from './pages/en/MentionsLegalesEN';
import PolitiqueConfidentialiteEN from './pages/en/PolitiqueConfidentialiteEN';
import CGUEN from './pages/en/CGUEN';
import CookiesEN from './pages/en/CookiesEN';
import DonneesPersonnellesEN from './pages/en/DonneesPersonnellesEN';
import CharteMentorEN from './pages/en/CharteMentorEN';

// AppInner detects OAuth Google callback via URL hash before rendering routes
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppInner() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <CookieBanner />
      <CrispChat />
      <PWAInstallBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/generate/:courseId" element={<ProtectedRoute><CourseGeneration /></ProtectedRoute>} />
        <Route path="/course/new" element={<ProtectedRoute><CourseCreator /></ProtectedRoute>} />
        <Route path="/course/:courseId" element={<ProtectedRoute><CourseOverview /></ProtectedRoute>} />
        <Route path="/course/:courseId/module/:moduleId" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
        <Route path="/course/:courseId/completion" element={<ProtectedRoute><Completion /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
        <Route path="/family" element={<ProtectedRoute><FamilyDashboard /></ProtectedRoute>} />
        <Route path="/org" element={<ProtectedRoute><OrgDashboard /></ProtectedRoute>} />
        <Route path="/bilan" element={<ProtectedRoute><BilanPage /></ProtectedRoute>} />

        {/* Articles & Blog */}
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="/articles/:slug" element={<ArticleDetail />} />

        {/* Forum communautaire */}
        <Route path="/communaute" element={<CommunityForum />} />
        <Route path="/communaute/nouveau" element={<ProtectedRoute><NewForumPost /></ProtectedRoute>} />
        <Route path="/communaute/:postId" element={<ForumPost />} />

        {/* Quiz Kurso */}
        <Route path="/quiz" element={<QuizBrowse />} />
        <Route path="/quiz/create" element={<ProtectedRoute><QuizCreate /></ProtectedRoute>} />
        <Route path="/quiz/:quizId/play" element={<QuizSolo />} />
        <Route path="/quiz/:pin/live" element={<QuizLive />} />
        <Route path="/quiz/:pin/present" element={<QuizPresent />} />

        {/* Part 5 — Contact & Support */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />

        {/* Part 4 — Mentors */}
        <Route path="/mentors" element={<Mentors />} />
        <Route path="/mentors/:mentorId" element={<MentorProfile />} />
        <Route path="/charte-mentor" element={<CharteMentor />} />

        {/* Part 6 — Référentiels */}
        <Route path="/referentiels" element={<Referentials />} />

        {/* Super Admin */}
        <Route path="/superadmin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
        <Route path="/superadmin/articles/new" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
        <Route path="/superadmin/articles/edit/:articleId" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />

        {/* Parcours */}
        <Route path="/parcours" element={<ProtectedRoute><Parcours /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
        <Route path="/course/:courseId/edit" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />

        {/* Vérification de certification (publique) */}
        <Route path="/certifications/verify/:hash" element={<CertificationVerify />} />

        {/* Pages légales FR */}
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/cgu" element={<CGU />} />
        <Route path="/cookies" element={<PolitiqueCookies />} />
        <Route path="/donnees-personnelles" element={<DonneesPersonnelles />} />

        {/* Pages légales EN */}
        <Route path="/en/mentions-legales" element={<MentionsLegalesEN />} />
        <Route path="/en/privacy-policy" element={<PolitiqueConfidentialiteEN />} />
        <Route path="/en/terms" element={<CGUEN />} />
        <Route path="/en/cookies" element={<CookiesEN />} />
        <Route path="/en/personal-data" element={<DonneesPersonnellesEN />} />
        <Route path="/en/mentor-charter" element={<CharteMentorEN />} />

        {/* Profil public — DOIT être la dernière route générique */}
        <Route path="/:slug" element={<PublicProfile />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <AuthProvider>
          <SpaceProvider>
            <BrowserRouter>
              <AppInner />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </SpaceProvider>
        </AuthProvider>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;


